import { useCallback } from "react";

import { ApiProblem } from "~/api/ports";
import { SignInScreen, type SignInCredentials, type SignInResult } from "~/screens/auth/signin-screen";

import { BOOTSTRAP_TENANT_ID } from "../bootstrap-tenant";
import { useClient } from "../client-context";

/**
 * The way into the product.
 *
 * This deployment is single-tenant: the screen never asks for a workspace id,
 * and the container injects `BOOTSTRAP_TENANT_ID`. It also never asks for a
 * TOTP code. `POST /session` still takes `tenant_id`, `email` and `password`
 * together; `totp_code` is omitted. The identity authority accepts that only
 * when the account has no authenticator enrolled — there is still no endpoint
 * that checks a password and then asks for a code.
 *
 * Nothing is persisted. The password lives in the screen's local state for the
 * length of one submit and leaves with the unmount.
 */
export default function SignInContainer() {
  const client = useClient();

  const onSignIn = useCallback(async (credentials: SignInCredentials): Promise<SignInResult> => {
    try {
      await client.signIn({
        tenantId: BOOTSTRAP_TENANT_ID,
        email: credentials.email,
        password: credentials.password,
      });
    } catch (error) {
      if (error instanceof ApiProblem) {
        return { ok: false, reason: error.status === 401 ? "invalid-credentials" : "error" };
      }
      throw error;
    }
    // A full document load, not a client-side navigation: the session cookie is
    // set on this response and every container in the shell is about to fetch
    // with it. Re-entering the app from scratch is the honest way to pick it up.
    // The demo entrypoint is `/demo.html`; assigning `/` would load the live BFF.
    window.location.assign(window.__INBOXFURY_DEMO ? "/demo.html" : "/");
    return { ok: true };
  }, [client]);

  return <SignInScreen onSignIn={onSignIn} />;
}
